/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { transformLabelToAnnotation } from 'src/core/utils/labelingUtils';
import { LanguageCard } from 'src/languageStudio/appShell/prebuiltDemos/components/LanguageCard';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { resultCardStyles, resultCardTitleStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import {
    DetectedLanguagesDocuments,
    LinkedEntity,
    LinkedEntityMatch
} from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import {
    LinkedEntityMatchesPair,
    SortTypes,
    getEntitiesSortOptions,
    getEntityId,
    getFilterCategories,
    sortLinkedEntitiesHelper
} from 'src/languageStudio/core/utils/demosUtils';
import { ModelType } from 'src/luis/types/ModelType';
import { Label } from 'src/modules/labeling/types/Label';
import { Labeler } from 'src/packages/react-text-annotator/lib/esm';

export type VisualizedLinkedEntitiesResultProps = {
    document: string;
    isLabelerRTL: boolean;
    isLabelerWordTokenized: boolean;
    entities: LinkedEntityMatchesPair[];
    detectedLanguage?: DetectedLanguagesDocuments;
};

const messages = defineMessages({
    showCardsLabel: {
        id: 'languageStudio.visualizedLinkedEntitiesResult.showCardsLabel',
        defaultMessage: 'Show cards'
    },
    onText: {
        id: 'languageStudio.visualizedLinkedEntitiesResult.onText',
        defaultMessage: 'On'
    },
    offText: {
        id: 'languageStudio.visualizedLinkedEntitiesResult.offText',
        defaultMessage: 'Off'
    },
    filter: {
        id: 'languageStudio.visualizedLinkedEntitiesResult.filter',
        defaultMessage: 'Filter'
    },
    sort: {
        id: 'languageStudio.visualizedLinkedEntitiesResult.sort',
        defaultMessage: 'Sort'
    },
    namedEntitiesIdentifiedTitle: {
        id: 'languageStudio.visualizedLinkedEntitiesResult.namedEntitiesIdentifiedTitle',
        defaultMessage: 'Linked entities identified'
    },
    entityCardTitle: {
        id: 'languageStudio.visualizedLinkedEntitiesResult.entityCardTitle',
        defaultMessage: 'Linked entity'
    },
    hideCardsMessage: {
        id: 'languageStudio.visualizedLinkedEntitiesResult.hideCardsMessage',
        defaultMessage: 'Turn on cards to see linked entities'
    }
});

const theme = getScopedTheme('LinkedEntitiesDemoPage');
const contextualMenuButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ menuIcon: { display: 'none' } });
const entityCardStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { width: '200px', minHeight: '110px' } });

export const VisualizedLinkedEntitiesResult = (props: VisualizedLinkedEntitiesResultProps) => {
    const { entities, document, isLabelerRTL, detectedLanguage, isLabelerWordTokenized } = props;
    const { localize } = useLocalization();

    const [showCards, setShowCards] = React.useState(true);
    const [filteredCategories, setFilteredCategories] = React.useState<string[]>([]);
    const [selectedSortType, setSelectedSortType] = React.useState<SortTypes>('appearanceAsc');

    const resultTokens = entities.map(e => ({
        id: getEntityId(e),
        tokens: { startTokenIndex: e.match.offset, endTokenIndex: e.match.offset + e.match.length - 1 }
    }));

    const filtersMenuItems: IContextualMenuItem[] = React.useMemo(
        () =>
            getFilterCategories(
                entities.map(e => e.entity),
                'name',
                filteredCategories,
                setFilteredCategories
            ),
        [entities, filteredCategories]
    );
    const sortMenuItems: IContextualMenuItem[] = getEntitiesSortOptions(selectedSortType, setSelectedSortType);

    const getLabel = (entity: LinkedEntityMatchesPair): Label => {
        const id = getEntityId(entity);
        const eTokens = resultTokens.find(t => t.id === id).tokens;

        return {
            depth: 1,
            renderingLevel: 1,
            renderedAs: 'line',
            end: eTokens.endTokenIndex,
            start: eTokens.startTokenIndex,
            intrinsicEnd: eTokens.endTokenIndex,
            intrinsicStart: eTokens.startTokenIndex,
            entity: { id, name: localize(messages.entityCardTitle), roles: [], typeId: ModelType.Simple }
        };
    };

    const entityCard = (linkedEntity: { entity: LinkedEntity; match: LinkedEntityMatch }) => (
        <Stack
            tokens={{ childrenGap: 7 }}
            key={getEntityId(linkedEntity)}
            styles={mergeStyleSets(resultCardStyles, entityCardStyles)}
            data-automation-id={getEntityId(linkedEntity)}>
            <LuisText fontWeight={500} styles={resultCardTitleStyles(theme.labelColor)}>
                {localize(messages.entityCardTitle)}
            </LuisText>
            <ExternalLink href={linkedEntity.entity.url}>
                {linkedEntity.match.text} -{linkedEntity.entity.dataSource}
            </ExternalLink>
        </Stack>
    );

    const annotations = React.useMemo(
        () =>
            entities
                .map(getLabel)
                .map(label =>
                    transformLabelToAnnotation(
                        label,
                        entityCard(entities.find(e => label.entity.id === getEntityId(e))),
                        () => theme.labelColor
                    )
                ),
        [entities]
    );

    return (
        <Stack styles={{ root: { width: '100%' } }} verticalAlign="end" tokens={{ childrenGap: 25 }}>
            <Stack horizontalAlign="end">
                <Stack horizontal grow horizontalAlign="end">
                    <Toggle
                        inlineLabel
                        checked={showCards}
                        onText={localize(messages.onText)}
                        offText={localize(messages.offText)}
                        styles={{ root: { marginBottom: 0 } }}
                        label={localize(messages.showCardsLabel)}
                        onChange={(_, checked) => setShowCards(checked)}
                    />
                    <ActionButton
                        disabled={!showCards}
                        text={localize(messages.filter)}
                        iconProps={{ iconName: 'Filter' }}
                        styles={contextualMenuButtonStyles}
                        menuProps={{ items: filtersMenuItems }}
                    />
                </Stack>
                <Stack styles={{ root: { width: '100%' } }} horizontal verticalAlign="center" horizontalAlign="space-between">
                    <LuisText as="h3" fontWeight={500}>
                        {localize(messages.namedEntitiesIdentifiedTitle)}
                    </LuisText>
                    <ActionButton
                        disabled={!showCards}
                        text={localize(messages.sort)}
                        menuProps={{ items: sortMenuItems }}
                        iconProps={{ iconName: 'Ascending' }}
                    />
                </Stack>
            </Stack>

            <Stack
                horizontal
                wrap
                styles={{ root: { marginTop: !showCards && '15px !important' } }}
                tokens={{ childrenGap: 20 }}
                data-automation-id="linkedEntityCardsContainer">
                {showCards ? (
                    entities
                        .filter(e => filteredCategories.length === 0 || filteredCategories.some(c => c === e.entity.name))
                        .sort((a, b) => sortLinkedEntitiesHelper(a, b, selectedSortType))
                        .map(entityCard)
                ) : (
                    <LuisText data-automation-id="hiddenCardsMessage" color={theme.secondaryTextColor}>
                        {localize(messages.hideCardsMessage)}
                    </LuisText>
                )}
            </Stack>

            <LanguageCard language={detectedLanguage?.documents[0].detectedLanguage} />

            <Stack tokens={{ childrenGap: 10 }}>
                <LuisText as="h3" fontWeight={500}>
                    {localize(commonDemosMessages.originalText)}
                </LuisText>
                <Labeler
                    text={document}
                    annotations={annotations}
                    labelerConfigs={{
                        isDisabled: true,
                        isRtl: isLabelerRTL,
                        tokenizationType: isLabelerWordTokenized ? 'word' : 'character'
                    }}
                />
            </Stack>
        </Stack>
    );
};
